/**
 * useLobbySocket — single source of truth for the lobby's realtime view.
 *
 * Manages the WebSocket lifecycle, dispatches events into reducer-style
 * state, and exposes typed sender functions for every action. Persists
 * `{ roomCode, charId, lastSeq }` to localStorage on every server event so
 * a reload mid-game can request a replay via lobby.replay.
 */
import { useEffect, useReducer, useRef } from 'react';
import type { LobbyView, ServerEvent, RoundResult } from '@tt/lobby';
import { feedback } from '@tt/feedback';
import { joinLobby } from '../../lib/api';

const REJOIN_KEY = 'tt:rejoin';

interface RejoinSnapshot { roomCode: string; charId: string; lastSeq: number; ts: number }

export function loadRejoin(): RejoinSnapshot | null {
  try {
    const raw = localStorage.getItem(REJOIN_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as RejoinSnapshot;
    if (!s.roomCode || !s.charId) return null;
    if (Date.now() - s.ts > 4 * 60 * 60 * 1000) return null;
    return s;
  } catch { return null; }
}
export function clearRejoin(): void {
  try { localStorage.removeItem(REJOIN_KEY); } catch { /* */ }
}
function saveRejoin(s: RejoinSnapshot): void {
  try { localStorage.setItem(REJOIN_KEY, JSON.stringify(s)); } catch { /* */ }
}

export interface ChatMsg { charId: string; name: string; text: string; at: string }
export interface Reaction { id: number; charId: string; name: string; emoji: string; at: number }
export interface VoteState { gameIds: string[]; endsAt: number; tally: Record<string, number> }
export interface FinalStandings {
  standings: Array<{ charId: string; name: string; points: number }>;
  awards: Array<{ charId: string; label: string }>;
  winnerHue: number;
}

export interface State {
  connected: boolean;
  /** True while the hook is actively trying to re-establish a dropped
   *  connection (between a non-clean WS close and a successful re-open).
   *  R1#7 — surfaced so consumers can render a "Reconnecting…" banner
   *  rather than a dead-silent UI. */
  reconnecting: boolean;
  /** True after the reconnect-attempt cap has been exhausted. The page
   *  reload escape valve in the parent should respond to this. */
  failed: boolean;
  lobby: LobbyView | null;
  chat: ChatMsg[];
  reactions: Reaction[];
  error: string | null;
  kicked: string | null;
  /** Current game match. */
  currentMatch: { matchId: string; gameId: string; startsAt: number; endsAt: number; params: unknown } | null;
  /** Last tick payload from the server, raw. */
  lastTick: { matchId: string; payload: unknown } | null;
  /** Last round result. */
  lastResult: RoundResult | null;
  vote: VoteState | null;
  /** Set has ended; show final standings pane. */
  finalStandings: FinalStandings | null;
  /** Tutorial card currently showing. */
  tutorial: { gameId: string; headline: string; tip: string } | null;
  /** Current in-game phase (multi-phase games). */
  currentPhase: { matchId: string; phase: string; phaseEndsAt?: number; payload?: unknown } | null;
}

type Action =
  | { type: 'connected'; v: boolean }
  | { type: 'reconnecting'; v: boolean }
  | { type: 'failed' }
  | { type: 'error'; code: string }
  | { type: 'lobby.state'; v: LobbyView }
  | { type: 'chat.msg'; v: ChatMsg }
  | { type: 'reaction'; v: Reaction }
  | { type: 'reaction.expire'; id: number }
  | { type: 'kicked'; reason: string }
  | { type: 'game.start'; v: NonNullable<State['currentMatch']> }
  | { type: 'game.tick'; matchId: string; payload: unknown }
  | { type: 'game.phase'; matchId: string; phase: string; phaseEndsAt?: number; payload?: unknown }
  | { type: 'game.end'; result: RoundResult }
  | { type: 'game.end.unmount' }
  | { type: 'vote.options'; gameIds: string[]; endsAt: number }
  | { type: 'vote.tally'; tally: Record<string, number> }
  | { type: 'set.final'; v: FinalStandings }
  | { type: 'tutorial'; v: { gameId: string; headline: string; tip: string } }
  | { type: 'tutorial.dismiss' };

function reducer(s: State, a: Action): State {
  switch (a.type) {
    case 'connected':
      // A successful (re)connect clears the reconnecting flag and the
      // failed flag, but leaves any prior error string for callers to
      // display until they choose to clear it themselves.
      return { ...s, connected: a.v, reconnecting: a.v ? false : s.reconnecting, failed: a.v ? false : s.failed };
    case 'reconnecting': return { ...s, reconnecting: a.v };
    case 'failed': return { ...s, failed: true, reconnecting: false, connected: false };
    case 'error': return { ...s, error: a.code };
    case 'lobby.state':
      return {
        ...s,
        lobby: a.v,
        finalStandings: a.v.state === 'finalStandings' ? s.finalStandings : null,
        currentMatch: a.v.state === 'waiting' ? null : s.currentMatch,
        vote: a.v.state === 'waiting' ? null : s.vote,
      };
    case 'chat.msg': return { ...s, chat: [...s.chat.slice(-49), a.v] };
    case 'reaction': return { ...s, reactions: [...s.reactions.slice(-9), a.v] };
    case 'reaction.expire': return { ...s, reactions: s.reactions.filter((r) => r.id !== a.id) };
    case 'kicked': return { ...s, kicked: a.reason };
    case 'game.start':
      return { ...s, currentMatch: a.v, lastTick: null, lastResult: null, vote: null, finalStandings: null, currentPhase: null };
    case 'game.tick':
      if (!s.currentMatch || s.currentMatch.matchId !== a.matchId) return s;
      return { ...s, lastTick: { matchId: a.matchId, payload: a.payload } };
    case 'game.phase':
      return { ...s, currentPhase: { matchId: a.matchId, phase: a.phase, ...(a.phaseEndsAt !== undefined ? { phaseEndsAt: a.phaseEndsAt } : {}), ...(a.payload !== undefined ? { payload: a.payload } : {}) } };
    case 'game.end':
      // Keep currentMatch alive for a few seconds after game.end so
      // the per-game outro card has time to play. The actual unmount
      // happens via the deferred 'game.end.unmount' action below.
      // lastResult is set immediately so post-round UI can read it.
      return { ...s, lastResult: a.result };
    case 'game.end.unmount':
      return { ...s, currentMatch: null };
    case 'vote.options':
      return { ...s, vote: { gameIds: a.gameIds, endsAt: a.endsAt, tally: {} } };
    case 'vote.tally':
      return { ...s, vote: s.vote ? { ...s.vote, tally: a.tally } : s.vote };
    case 'set.final':
      return { ...s, finalStandings: a.v };
    case 'tutorial':
      return { ...s, tutorial: a.v };
    case 'tutorial.dismiss':
      return { ...s, tutorial: null };
    default: return s;
  }
}

const INITIAL: State = {
  connected: false, reconnecting: false, failed: false,
  lobby: null, chat: [], reactions: [],
  error: null, kicked: null, currentMatch: null, lastTick: null,
  lastResult: null, vote: null, finalStandings: null, tutorial: null,
  currentPhase: null,
};

export interface LobbySocketApi extends State {
  ready(next: boolean): void;
  start(): void;
  newSession(): void;
  leave(): void;
  setSettings(patch: Record<string, unknown>): void;
  kick(charId: string): void;
  freeLaunch(gameId: string): void;
  voteCast(gameId: string): void;
  skipRoundEnd(): void;
  sendInput(payload: unknown): void;
  /** Send multiple inputs as one Lambda invocation. The server
   *  iterates them against a single state load. Per
   *  netcode-revamp.md §VIII.5. Each payload gets auto-injected
   *  with its own _seq so the source client can ack each one
   *  independently. */
  sendInputBatch(payloads: unknown[]): void;
  sendHeartbeat(): void;
  sendChat(text: string): void;
  sendReaction(emoji: string): void;
  refreshPresence(): void;
  dismissTutorial(): void;
  rawSend(action: string, extra?: Record<string, unknown>): void;
  /** Highest input seq the server has acknowledged for THIS client.
   *  Per netcode-revamp.md §III.1 and §VIII.1. Used by movement-game
   *  clients to drop replayed-but-acked inputs from their reconciliation
   *  queue. Returns 0 until the server has acked anything. */
  getLastInputAck(): number;
}

interface Args {
  wsUrl: string;
  ticket: string;
  enabled: boolean;
  charId: string;
  /** If reconnecting, the last seq we saw. */
  sinceSeq?: number | undefined;
}

export function useLobbySocket({ wsUrl, ticket, enabled, charId, sinceSeq }: Args): LobbySocketApi {
  const wsRef = useRef<WebSocket | null>(null);
  const seqRef = useRef<number>(sinceSeq ?? 0);
  const reactionIdRef = useRef<number>(1);
  /** Per-client outgoing input sequence counter. Bumped on every
   *  sendInput; injected as `_seq` into the payload so the server can
   *  echo it back on the resulting tick as `ack`. */
  const inputSeqRef = useRef<number>(0);
  /** Highest input seq the server has acknowledged for THIS client.
   *  Updated from any inbound game.tick that carries an `ack` field. */
  const lastAckRef = useRef<number>(0);
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const stateRef = useRef<State>(INITIAL);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    if (!enabled || !wsUrl || !ticket) return;

    // R1#7 — exponential-backoff reconnect.
    //
    // Replaces the previous `window.location.reload()` escape hatch.
    // iOS aggressively backgrounds tabs; the old behaviour burned a full
    // bundle reload every time a user came back to the tab, killing
    // animations and audio context. Now we keep the React tree alive and
    // only re-establish the WS layer.
    //
    // Lifecycle:
    //   - `connect(ticketStr)` opens a socket, wires listeners.
    //   - On non-clean close, schedule a reconnect with backoff.
    //   - Reconnect: mint a fresh ticket via joinLobby(roomCode) since
    //     the original ticket is one-shot (burned by ws-connect on
    //     successful handshake), then call connect() with the new ticket.
    //   - After MAX_ATTEMPTS, dispatch 'failed' and stop.
    //   - Cleanup: cancel any pending timer + close any open socket.
    const BACKOFF_MS = [500, 1000, 2000, 4000, 8000, 8000, 8000, 8000];
    const MAX_ATTEMPTS = BACKOFF_MS.length;

    let cancelled = false;
    let attempt = 0;
    let reconnectTimer: number | null = null;
    let hbTimer: number | null = null;
    let livenessTimer: number | null = null;
    /** roomCode captured from inbound lobby.state. Required by the
     *  reconnect path because joinLobby() needs the code; we can't
     *  recover it from the burnt ticket. Falls back to loadRejoin()
     *  when the user reconnects before any lobby.state has arrived
     *  (rare, but possible if the very first connect dies before open). */
    let roomCodeRef: string | null = null;

    function clearTimers() {
      if (hbTimer !== null) { window.clearInterval(hbTimer); hbTimer = null; }
      if (livenessTimer !== null) { window.clearInterval(livenessTimer); livenessTimer = null; }
    }

    function scheduleReconnect(): void {
      if (cancelled) return;
      if (reconnectTimer !== null) return; // already scheduled
      if (attempt >= MAX_ATTEMPTS) {
        dispatch({ type: 'failed' });
        return;
      }
      const delay = BACKOFF_MS[attempt] ?? 8000;
      attempt += 1;
      dispatch({ type: 'reconnecting', v: true });
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        if (cancelled) return;
        void doReconnect();
      }, delay);
    }

    async function doReconnect(): Promise<void> {
      if (cancelled) return;
      // Resolve a roomCode: prefer the one captured from lobby.state
      // (most accurate); fall back to the saved rejoin snapshot.
      const code = roomCodeRef ?? loadRejoin()?.roomCode ?? null;
      if (!code) {
        // No way to mint a fresh ticket — give up. Caller can show the
        // page-reload escape valve via the `failed` state.
        dispatch({ type: 'failed' });
        return;
      }
      try {
        const fresh = await joinLobby(code);
        if (cancelled) return;
        connect(fresh.ticket, fresh.wsUrl);
      } catch {
        if (cancelled) return;
        // Mint failed (network, expired session, room gone). Try again
        // until the cap is hit.
        scheduleReconnect();
      }
    }

    function connect(ticketStr: string, wsUrlStr: string): void {
      if (cancelled) return;
      clearTimers();
      const ws = new WebSocket(`${wsUrlStr}?ticket=${encodeURIComponent(ticketStr)}`);
      wsRef.current = ws;

      let lastPongAt = Date.now();
      // Liveness check: if we don't see a pong for >6s while the socket
      // claims to be OPEN, treat the link as dead and force-close. The
      // close handler funnels into the reconnect path. (Old code reloaded
      // the page here; reconnect is much cheaper.)
      livenessTimer = window.setInterval(() => {
        if (ws.readyState !== WebSocket.OPEN) return;
        const stale = Date.now() - lastPongAt;
        if (stale > 6000) {
          try { ws.close(); } catch { /* */ }
          // close handler will schedule the reconnect.
        }
      }, 1500);

      ws.addEventListener('open', () => {
        lastPongAt = Date.now();
        // A successful open resets backoff so a long-lived socket that
        // drops once recovers in 500ms instead of inheriting the previous
        // attempt count.
        attempt = 0;
        dispatch({ type: 'connected', v: true });
        // ALWAYS send lobby.join — handleJoin reattaches the seat to the
        // new connId by charId. handleReplay alone leaves the PLAYER row
        // pointed at the dead connId, which manifested as the "infinite
        // connecting screen" bug because the lobby never recognizes the
        // rejoiner. Replay piggybacks for missed events when applicable.
        ws.send(JSON.stringify({ action: 'lobby.join' }));
        // R1#7 — replay covers BOTH the initial-mount sinceSeq AND the
        // accumulated seq from a prior connection that dropped mid-game.
        // seqRef.current is updated on every server event, so it
        // naturally tracks the high-water mark.
        const since = seqRef.current;
        if (since > 0) {
          ws.send(JSON.stringify({ action: 'lobby.replay', sinceSeq: since }));
        }
      });

      ws.addEventListener('close', () => {
        // Tear down per-socket timers first so they don't leak across
        // reconnect attempts.
        clearTimers();
        if (cancelled) return;
        dispatch({ type: 'connected', v: false });
        // Schedule a reconnect — non-clean drop. Intentional teardown
        // sets `cancelled` first via the cleanup function, so a manual
        // leave won't trigger this.
        scheduleReconnect();
      });
      ws.addEventListener('error', () => dispatch({ type: 'error', code: 'connection-error' }));

      ws.addEventListener('message', (ev) => {
        let msg: ServerEvent;
        try { msg = JSON.parse(String(ev.data)) as ServerEvent; }
        catch { return; }
        if ('seq' in msg && typeof (msg as { seq?: number }).seq === 'number') {
          const eventSeq = (msg as { seq: number }).seq;
          const isGameroomTickSeq = msg.event === 'game.tick' && eventSeq >= 1_000_000;
          if (!isGameroomTickSeq) seqRef.current = Math.max(seqRef.current, eventSeq);
        }
        switch (msg.event) {
          case 'lobby.state': {
            dispatch({ type: 'lobby.state', v: msg.state });
            if (msg.state.roomCode && charId) {
              roomCodeRef = msg.state.roomCode;
              saveRejoin({ roomCode: msg.state.roomCode, charId, lastSeq: seqRef.current, ts: Date.now() });
            }
            const active = msg.state.activeMatch;
            const current = stateRef.current.currentMatch;
            if (active && (!current || current.matchId !== active.matchId)) {
              const receivedAt = Date.now();
              const cdMs = Math.max(0, active.startsAt - receivedAt);
              const durMs = Math.max(0, active.endsAt - active.startsAt);
              const localStartsAt = receivedAt + cdMs;
              const localEndsAt = localStartsAt + durMs;
              dispatch({
                type: 'game.start',
                v: { matchId: active.matchId, gameId: active.gameId, startsAt: localStartsAt, endsAt: localEndsAt, params: active.params },
              });
            }
            break;
          }
          case 'chat.msg': dispatch({ type: 'chat.msg', v: { charId: msg.charId, name: msg.name, text: msg.text, at: msg.at } }); break;
          case 'reaction': {
            const id = reactionIdRef.current++;
            dispatch({ type: 'reaction', v: { id, charId: msg.charId, name: msg.name, emoji: msg.emoji, at: msg.at } });
            window.setTimeout(() => dispatch({ type: 'reaction.expire', id }), 2000);
            break;
          }
          case 'kicked': dispatch({ type: 'kicked', reason: msg.reason }); clearRejoin(); break;
          case 'game.start': {
            // CRITICAL: derive startsAt/endsAt from RECEIPT TIME + durations.
            // Server's absolute timestamps drift across devices via clock skew.
            // Relative durations are bulletproof.
            const m = msg as unknown as { matchId: string; gameId: string; countdownMs?: number; durationMs?: number; startsAt: number; endsAt: number; params: unknown };
            const receivedAt = Date.now();
            const cdMs = typeof m.countdownMs === 'number' ? m.countdownMs : Math.max(0, m.startsAt - receivedAt);
            const durMs = typeof m.durationMs === 'number' ? m.durationMs : Math.max(0, m.endsAt - m.startsAt);
            const localStartsAt = receivedAt + cdMs;
            const localEndsAt = localStartsAt + durMs;
            dispatch({ type: 'game.start', v: { matchId: m.matchId, gameId: m.gameId, startsAt: localStartsAt, endsAt: localEndsAt, params: m.params } });
            break;
          }
          case 'game.tick': {
            // Envelope-level ack: server-side handleGameInput attaches
            // `ack` to the tick when the input that caused it carried a
            // `_seq`. Bubble the ack into lastAckRef so movement games
            // can use it for reconciliation. Per netcode-revamp.md §VIII.1.
            const ackField = (msg as { ack?: number }).ack;
            if (typeof ackField === 'number' && ackField > lastAckRef.current) {
              lastAckRef.current = ackField;
            }
            const ackBySlot = (msg as { ackBySlot?: Record<string, number> }).ackBySlot;
            if (ackBySlot) {
              const mySlot = stateRef.current.lobby?.players.find((p) => p.charId === charId)?.slot;
              const slotAck = mySlot !== undefined ? ackBySlot[String(mySlot)] : undefined;
              if (typeof slotAck === 'number' && slotAck > lastAckRef.current) {
                lastAckRef.current = slotAck;
              }
            }
            dispatch({ type: 'game.tick', matchId: msg.matchId, payload: msg.payload });
            break;
          }
          case 'game.phase': {
            const m = msg as unknown as { matchId: string; phase: string; phaseEndsAt?: number; payload?: unknown };
            dispatch({
              type: 'game.phase',
              matchId: m.matchId,
              phase: m.phase,
              ...(m.phaseEndsAt !== undefined ? { phaseEndsAt: m.phaseEndsAt } : {}),
              ...(m.payload !== undefined ? { payload: m.payload } : {}),
            });
            break;
          }
          case 'game.end':
            dispatch({ type: 'game.end', result: msg.result });
            feedback.cheer();
            // Defer the actual unmount of the GameStage so the per-game
            // outro card (4s default) has time to slide in, hold, and
            // slide out before the canvas is destroyed. Without this,
            // currentMatch flipped to null instantly on game.end and
            // GameStage's effect cleanup ran the per-game stop() which
            // tore down the Pixi stage AND killed the outro card mid-animation.
            window.setTimeout(() => dispatch({ type: 'game.end.unmount' }), 3200);
            break;
          case 'vote.options': dispatch({ type: 'vote.options', gameIds: msg.gameIds, endsAt: msg.endsAt }); break;
          case 'vote.tally': dispatch({ type: 'vote.tally', tally: msg.tally }); break;
          case 'set.final':
            dispatch({ type: 'set.final', v: { standings: msg.standings, awards: msg.awards, winnerHue: msg.winnerHue } });
            break;
          case 'tutorial':
            dispatch({ type: 'tutorial', v: { gameId: msg.gameId, headline: msg.pitch, tip: msg.tip } });
            break;
          case 'pong': lastPongAt = Date.now(); break;
          case 'error':
            dispatch({ type: 'error', code: msg.code });
            // Server lost our CONN row. Closing triggers the reconnect
            // path which mints a fresh ticket — much cleaner than the
            // page-reload bandaid this used to be.
            if (msg.code === 'no-conn') {
              try { ws.close(); } catch { /* */ }
            }
            break;
        }
      });

      // Heartbeat every 1s.
      hbTimer = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ action: 'game.heartbeat' }));
      }, 1000);
    }

    // Initial connection uses the seed ticket from props.
    connect(ticket, wsUrl);

    return () => {
      cancelled = true;
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      clearTimers();
      const ws = wsRef.current;
      try { ws?.close(); } catch { /* ignore */ }
      wsRef.current = null;
    };
  }, [wsUrl, ticket, enabled, charId, sinceSeq]);

  function send(action: string, extra: Record<string, unknown> = {}): void {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ action, ...extra }));
  }

  return {
    ...state,
    ready: (next) => send('lobby.ready', { ready: next }),
    start: () => send('lobby.start'),
    newSession: () => send('set.new'),
    leave: () => { send('lobby.leave'); clearRejoin(); },
    setSettings: (patch) => send('lobby.settings.set', { patch }),
    kick: (cid) => send('lobby.kick', { charId: cid }),
    freeLaunch: (gameId) => send('free.launch', { gameId }),
    voteCast: (gameId) => send('vote.cast', { gameId }),
    skipRoundEnd: () => send('roundEnd.skip'),
    sendInput: (payload) => {
      const seq = ++inputSeqRef.current;
      const wrapped = payload && typeof payload === 'object'
        ? { ...(payload as Record<string, unknown>), _seq: seq }
        : { _v: payload, _seq: seq };
      send('game.input', { payload: wrapped });
    },
    sendInputBatch: (payloads) => {
      if (!Array.isArray(payloads) || payloads.length === 0) return;
      const wrapped = payloads.map((payload) => {
        const seq = ++inputSeqRef.current;
        return payload && typeof payload === 'object'
          ? { ...(payload as Record<string, unknown>), _seq: seq }
          : { _v: payload, _seq: seq };
      });
      send('game.input.batch', { payloads: wrapped });
    },
    sendHeartbeat: () => send('game.heartbeat'),
    sendChat: (text) => send('chat.msg', { text }),
    sendReaction: (emoji) => send('reaction', { emoji }),
    refreshPresence: () => send('presence.refresh'),
    dismissTutorial: () => dispatch({ type: 'tutorial.dismiss' }),
    rawSend: send,
    getLastInputAck: () => lastAckRef.current,
  };
}
